import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@mui/material';

import Page from '@/components/Page';
import Card from '@/components/Card';
import Button from '@/components/Button';
import Stack from '@/components/Stack';
import LoadingState from '@/components/LoadingState';
import Pagination from '@/components/Pagination';

import AddressTester from './AddressTester';
import CreateRegionModal from './CreateRegionModal';
import RegionDetail from './RegionDetail';
import { fetchRegions } from './api';
import type { Region, Pagination as PaginationType } from './types';

const Regions = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [pagination, setPagination] = useState<PaginationType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [stateFilter, setStateFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);

  const loadRegions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetchRegions({
        page,
        per_page: 25,
        ...(stateFilter ? { state: stateFilter } : {}),
        ...(activeFilter !== '' ? { is_active: activeFilter === 'true' } : {}),
      });
      setRegions(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      setError(
        err.response?.data?.errors?.join(', ') ||
          err.message ||
          'Failed to load regions',
      );
    } finally {
      setLoading(false);
    }
  }, [page, stateFilter, activeFilter]);

  useEffect(() => {
    loadRegions();
  }, [loadRegions]);

  const handleRegionCreated = () => {
    loadRegions();
  };

  const getTaxTypeColor = (taxType: string) => {
    switch (taxType) {
      case 'w2':
        return 'primary';
      case '1099':
        return 'warning';
      case 'all':
        return 'success';
      default:
        return 'default';
    }
  };

  const uniqueStates = Array.from(new Set(regions.map((r) => r.state))).sort();

  if (selectedRegionId) {
    return (
      <Page title="Region Detail">
        <RegionDetail
          regionId={selectedRegionId}
          onBack={() => setSelectedRegionId(null)}
          onUpdated={loadRegions}
        />
      </Page>
    );
  }

  return (
    <Page title="Region Management">
      <AddressTester />

      <Box sx={{ mt: 3 }}>
        <Card>
          <Card.Section>
            <Stack align="center" justify="apart">
              <Typography variant="h6">Regions</Typography>
              <Button
                a11yLabel="Add region"
                label="Add Region"
                onClick={() => setShowCreateModal(true)}
              />
            </Stack>

            <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>State</InputLabel>
                <Select
                  value={stateFilter}
                  label="State"
                  onChange={(e) => {
                    setStateFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All States</MenuItem>
                  {uniqueStates.map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={activeFilter}
                  label="Status"
                  onChange={(e) => {
                    setActiveFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {loading ? (
              <LoadingState />
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>Tax Type</TableCell>
                        <TableCell align="right">Zip Codes</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {regions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            No regions found
                          </TableCell>
                        </TableRow>
                      ) : (
                        regions.map((region) => (
                          <TableRow
                            key={region.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setSelectedRegionId(region.id)}
                          >
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {region.name}
                              </Typography>
                            </TableCell>
                            <TableCell>{region.state}</TableCell>
                            <TableCell>
                              <Chip
                                label={region.job_tax_type.toUpperCase()}
                                color={getTaxTypeColor(region.job_tax_type)}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              {region.zipcode_count}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={region.is_active ? 'Active' : 'Inactive'}
                                color={region.is_active ? 'success' : 'default'}
                                size="small"
                                variant={
                                  region.is_active ? 'filled' : 'outlined'
                                }
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pagination && pagination.total_pages > 1 && (
                  <Box
                    sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}
                  >
                    <Pagination
                      currentPage={page}
                      totalPages={pagination.total_pages}
                      onPageChange={setPage}
                    />
                  </Box>
                )}
              </>
            )}
          </Card.Section>
        </Card>
      </Box>

      <CreateRegionModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={handleRegionCreated}
      />
    </Page>
  );
};

export default Regions;
